import React, { useState, useEffect } from "react";
import { db, storage } from "../../utils/Firebase-Config";
import { collection, query, where, getDocs, addDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { UserAuth } from "../../utils/AuthContext";
import AddToPhotosIcon from '@mui/icons-material/AddToPhotos';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

const obfuscateUrl = (plainUrl) => {
  if (!plainUrl) return "";
  const reversed = plainUrl.split("").reverse().join("");
  return btoa(reversed);
};

const deobfuscateUrl = (scrambledUrl) => {
  if (!scrambledUrl) return "";
  try {
    const decoded = atob(scrambledUrl);
    return decoded.split("").reverse().join("");
  } catch (err) {

    return scrambledUrl;
  }
};

function GalleryUpload() {
  const { user } = UserAuth();

  const [portfolioDocId, setPortfolioDocId] = useState(null);
  const [galleryItems, setGalleryItems] = useState([]);
  const [title, setTitle] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);

  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");

  useEffect(() => {
    if (!user) return;

    const fetchPortfolioAndItems = async () => {
      try {
        const q = query(collection(db, "portfolios"), where("ownerUid", "==", user.uid));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const portfolioDoc = querySnapshot.docs[0];
          setPortfolioDocId(portfolioDoc.id);

          const portfolioSubRef = collection(db, "portfolios", portfolioDoc.id, "portfolio");
          const itemsSnapshot = await getDocs(portfolioSubRef);
          const items = itemsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setGalleryItems(items);
        } else {
          setStatus("Create a portfolio subdomain first to add projects!");
        }
      } catch (err) {
        console.error("Error fetching portfolio items:", err);
      }
    };

    fetchPortfolioAndItems();
  }, [user]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile || !portfolioDocId) return;

    setLoading(true);
    setStatus("Uploading image to storage...");

    try {
      const uniqueFileName = `${Date.now()}_${selectedFile.name}`;
      const storageRef = ref(storage, `portfolios/${portfolioDocId}/gallery/${uniqueFileName}`);
      const uploadResult = await uploadBytes(storageRef, selectedFile);
      const downloadUrl = await getDownloadURL(uploadResult.ref);

      setStatus("Obfuscating assets...");
      
      const scrambledUrl = obfuscateUrl(downloadUrl);

      setStatus("Creating portfolio image document...");

      const portfolioSubRef = collection(db, "portfolios", portfolioDocId, "portfolio");
      const newItemDoc = await addDoc(portfolioSubRef, {
        imageUrl: scrambledUrl, // Saved securely as a scrambled string
        title: title || "Untitled Project",
        createdAt: new Date().toISOString()
      });

      setGalleryItems((prev) => [
        ...prev,
        { id: newItemDoc.id, imageUrl: scrambledUrl, title: title || "Untitled Project" }
      ]);
      setSelectedFile(null);
      setTitle("");
      setStatus("🎉 Image successfully added to your portfolio!");
    } catch (error) {
      console.error("Error uploading project:", error);
      setStatus("❌ Upload failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!portfolioDocId && !status) {
    return <p>Loading gallery editor...</p>;
  }

  return (
    <div className="uploader-wrapper" >
      <h3 style={{ margin: "0 0 15px 0" }}>Add Portfolio Image</h3>
      
      {portfolioDocId && (
        <form onSubmit={handleUpload}>
          <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>Project Title:</label>
          <input
            type="text"
            placeholder="e.g. Threat Intel Dashboard"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={loading}
            style={{ width: "100%", padding: "8px", marginBottom: "15px", display: "block" }}
            required
          />

          <input
            type="file"
            accept="image/*"
            disabled={loading}
            onChange={(e) => setSelectedFile(e.target.files[0])}
            style={{ marginBottom: "15px", display: "block" }}
            required
          />

          <button
            type="submit"
            disabled={loading}
          >
            {loading ? <CloudUploadIcon /> : <AddToPhotosIcon />}
          </button>
        </form>
      )}

      {status && <p style={{ marginTop: "15px", fontSize: "14px", fontWeight: "600" }}>{status}</p>}

      {/* Gallery Grid Preview */}
      <h4 style={{ margin: "25px 0 10px 0" }}>Your Portfolio Preview</h4>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px" }}>
        {galleryItems.map((item) => (
          <div key={item.id} style={{ textAlign: "center" }}>
            <img
              src={deobfuscateUrl(item.imageUrl)} 
              alt={item.title}
              style={{ width: "100%", height: "100px", objectFit: "cover", borderRadius: "8px" }}
            />
            <span style={{ fontSize: "11px", display: "block", marginTop: "5px", color: "#6b7280" }}>{item.title}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default GalleryUpload;
